/* One native Shopify form, one selected variant, one allocated selling plan.
 * Prices and plan IDs are rendered by Liquid in the shopper's current market.
 * No API calls, polling, cart mutations or third-party widget dependency. */
(() => {
  if (customElements.get('neurofuel-offer')) return;
  class NeurofuelOffer extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-nf-submit]');
      this.status = this.querySelector('[data-nf-status]');
      // Install the guard before parsing or enabling any purchase controls.
      this.form.addEventListener('submit', e => {
        if (!this.valid || !this.commit()) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, {capture:true,signal});
      this.form.addEventListener('formdata', e => {
        // Also covers FormData consumers (native theme, sticky button).
        const plan = this.currentPlan();
        if (!this.valid || !this.variant?.available || (this.mode === 'subscription' && !plan)) {
          e.formData.delete('id'); e.formData.delete('selling_plan'); return;
        }
        e.formData.set('id', this.variant.id);
        e.formData.set('quantity', '1');
        if (plan && this.mode === 'subscription') e.formData.set('selling_plan', plan.id);
        else e.formData.delete('selling_plan');
      }, {signal});
      try {
        this.config = JSON.parse(this.querySelector('[data-config]').textContent);
        if (this.config.productId !== '15472901357912' || this.config.variants.length !== 9) throw Error('Wrong offer');
        const ids = new Set();
        for (const v of this.config.variants) {
          if (ids.has(v.id) || !/^\d+$/.test(v.id) || ![1,2,3].includes(v.tier) || !Array.isArray(v.plans) || !Array.isArray(v.options) || !Number.isFinite(v.price)) throw Error('Invalid offer');
          ids.add(v.id);
          if (v.plans.some(p => !/^\d+$/.test(p.id) || !Number.isFinite(p.price))) throw Error('Invalid plan');
        }
        this.variant = this.config.variants.find(v => v.id === this.config.selectedId) || this.config.variants.find(v => v.tier === 3) || this.config.variants[0];
        this.flavours = (this.variant.tier === 3 ? this.config.variants.find(v => v.tier === 1) : this.variant).options.slice(0,2);
        this.mode = this.variant.plans.length ? 'subscription' : 'once';
        this.planId = this.variant.plans.find(p => p.selected === true)?.id || '';
        this.addEventListener('click', e => {
          const button = e.target.closest('button');
          if (!button || button.disabled || !this.contains(button)) return;
          if (button.dataset.nfTier) {
            const next = this.forTier(Number(button.dataset.nfTier));
            if (next) this.variant = next;
          } else if (button.dataset.choice) {
            const flavours = [...this.flavours]; flavours[Number(button.dataset.family)] = button.dataset.choice;
            const next = this.config.variants.find(v => v.tier === this.variant.tier && flavours.every((x,i) => x === v.options[i]));
            if (next) { this.flavours = flavours; this.variant = next; }
          } else if (button.dataset.nfPurchase) this.mode = button.dataset.nfPurchase;
          else return;
          this.render();
        }, {signal});
        this.querySelector('[data-nf-plan]').addEventListener('change', e => {
          const plan = this.variant.plans.find(p => p.id === e.target.value);
          if (!plan) return;
          this.planId = plan.id; this.planPreference = this.planKey(plan); this.render();
        }, {signal});
        this.render();
        this.dataset.ready = 'true';
      } catch (_) {
        this.valid = false; this.dataset.ready = 'false';
        this.submitButton.disabled = true;
        this.form.querySelector('[name="id"]').disabled = true;
        this.status.textContent = this.config?.labels?.load_error || 'The bundle selector could not load. Please refresh the page.';
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; this.valid = false; }
    forTier(tier) {
      return this.config.variants.find(v => v.tier === tier && (tier === 3 || this.flavours.every((x,i) => x === v.options[i])));
    }
    planKey(plan) {
      for (const value of plan?.options || []) {
        try {
          const p = JSON.parse(value), b = p.billing, d = p.delivery;
          const valid = x => x && ['DAY','WEEK','MONTH','YEAR'].includes(x.interval) && Number.isInteger(x.frequency) && x.frequency > 0;
          if (valid(b) && valid(d)) return JSON.stringify([b.interval,b.frequency,d.interval,d.frequency]);
        } catch (_) {}
      }
      return String(plan?.name || '').trim().toLowerCase();
    }
    choosePlan(variant) {
      return variant.plans.find(p => p.id === this.planId) || variant.plans.find(p => this.planKey(p) === this.planPreference) || variant.plans[0];
    }
    currentPlan() { return this.variant?.plans.find(p => p.id === this.planId); }
    label(plan) {
      try {
        const [bu,bn,du,dn] = JSON.parse(this.planKey(plan));
        if (bu === 'WEEK' && du === 'WEEK' && bn === dn) return this.config.labels.every_weeks.replace('[count]',String(bn));
      } catch (_) {}
      return plan.name;
    }
    commit() {
      const v = this.variant, plan = this.currentPlan();
      if (!this.valid || !v.available || (this.mode === 'subscription' && !plan) || (this.mode === 'once' && this.config.requiresPlan)) return false;
      const id = this.form.querySelector('[name="id"]'), sp = this.form.querySelector('[name="selling_plan"]');
      id.disabled = false; id.value = v.id;
      this.form.querySelector('[name="quantity"]').value = '1';
      sp.disabled = this.mode !== 'subscription'; sp.value = sp.disabled ? '' : plan.id;
      return true;
    }
    render() {
      const v = this.variant, labels = this.config.labels;
      if (this.mode === 'subscription' && !v.plans.length) this.mode = 'once';
      if (this.config.requiresPlan && this.mode === 'once' && v.plans.length) this.mode = 'subscription';
      const plan = this.choosePlan(v); this.planId = plan?.id || '';
      if (plan) this.planPreference = this.planKey(plan);
      this.valid = v.available && (this.mode === 'subscription' ? !!plan : !this.config.requiresPlan);
      this.querySelectorAll('[data-nf-tier]').forEach(b => {
        const candidate = this.forTier(Number(b.dataset.nfTier));
        b.disabled = !candidate?.available; b.setAttribute('aria-pressed',String(candidate?.id === v.id));
        const badge = b.querySelector('[data-off]'), allocation = candidate && this.choosePlan(candidate);
        const price = this.mode === 'subscription' && allocation ? allocation.price : candidate?.price;
        const off = candidate?.compare > price ? Math.round(100-price/candidate.compare*100) : 0;
        badge.hidden = !off; badge.textContent = (labels.save || 'SAVE [percent]%').replace('[percent]',String(off));
      });
      const each = v.tier === 3;
      this.querySelectorAll('[data-choice]').forEach(b => {
        b.disabled = each;
        b.setAttribute('aria-pressed',String(each || b.dataset.choice === this.flavours[Number(b.dataset.family)]));
      });
      this.querySelectorAll('[data-nf-purchase]').forEach(b => {
        const sub = b.dataset.nfPurchase === 'subscription', amount = sub ? plan : v;
        b.hidden = sub ? !v.plans.length : this.config.requiresPlan;
        b.disabled = !v.available || !amount;
        b.setAttribute('aria-pressed',String(this.mode === b.dataset.nfPurchase));
        b.querySelector('[data-price]').textContent = amount?.priceText || '—';
        const compare = b.querySelector('[data-compare]');
        compare.hidden = !amount || v.compare <= amount.price; compare.textContent = v.compareText;
      });
      const sub = this.mode === 'subscription', select = this.querySelector('[data-nf-plan]');
      select.replaceChildren(...v.plans.map(p => {const o = document.createElement('option');o.value = p.id;o.textContent = this.label(p);o.selected = p.id === this.planId;return o;}));
      select.disabled = !sub || !this.valid;
      this.querySelector('[data-nf-sub-details]').hidden = !sub;
      this.querySelector('[data-nf-once-note]').hidden = sub;
      const discount = plan && v.price > plan.price ? Math.round(100-plan.price/v.price*100) : 0;
      const extra = this.querySelector('[data-extra]'); extra.hidden = !discount; extra.textContent = labels.extra_sub_discount.replace('[percent]',String(discount));
      const saving = this.querySelector('[data-saving]'); saving.hidden = !plan || v.compare <= plan.price; saving.querySelector('strong').textContent = plan?.savingText || '';
      this.querySelector('[data-nf-atc-price]').textContent = (sub ? plan : v)?.priceText || '—';
      this.submitButton.disabled = !this.valid;
      this.status.textContent = this.valid ? '' : v.available ? labels.mix_unavailable : labels.sold_out;
      if (!this.commit()) { this.form.querySelector('[name="id"]').disabled = true; this.form.querySelector('[name="selling_plan"]').disabled = true; }
      // Let the existing sticky bar reflect the same real form. No competing variant-picker event.
      this.form.dispatchEvent(new Event('change',{bubbles:true}));
    }
  }
  customElements.define('neurofuel-offer',NeurofuelOffer);
})();
